import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { formatCurrency } from '../lib/utils';
import { Loader2, ChevronLeft, ChevronRight, Printer, Cake, Croissant } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Return midnight Sunday of the week containing the given date (browser-local).
const getSundayOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // getDay: Sun=0
  return d;
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDay = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default function WeeklyReport() {
  const { language } = useLanguage();
  const T = (ar, en) => (language === 'ar' ? ar : en);

  const [weekStart, setWeekStart] = useState(() => toISODate(getSundayOfWeek(new Date())));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/reports/weekly`, {
        params: { week_start: weekStart },
      });
      setReport(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || T('فشل تحميل التقرير', 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const shiftWeek = (days) => {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + days);
    setWeekStart(toISODate(getSundayOfWeek(d)));
  };

  const handlePrint = () => {
    if (!report) return;
    const html = buildPrintHTML(report, language);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url);
    w.onload = () => {
      w.print();
      setTimeout(() => {
        w.close();
        URL.revokeObjectURL(url);
      }, 500);
    };
  };

  const renderItemTable = (rows, title, Icon, accent) => (
    <Card className="border-border/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon size={20} className={accent} />
          {title}
          <span className="ms-auto text-sm font-normal text-muted-foreground">
            {rows.length} {T('عنصر', 'items')}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground text-sm">
            {T('لا توجد بيانات', 'No data')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{T('المنتج', 'Item')}</TableHead>
                <TableHead className="text-end">{T('الكمية', 'Qty')}</TableHead>
                <TableHead className="text-end">{T('عدد مرات البيع', 'Times sold')}</TableHead>
                <TableHead className="text-end">{T('الإيرادات', 'Revenue')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.product_name}>
                  <TableCell className="font-medium">{r.product_name}</TableCell>
                  <TableCell className="text-end">{r.quantity}</TableCell>
                  <TableCell className="text-end text-muted-foreground">{r.times_sold}</TableCell>
                  <TableCell className="text-end font-bold text-primary">
                    {formatCurrency(r.revenue)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/40 font-semibold">
                <TableCell>{T('الإجمالي', 'Total')}</TableCell>
                <TableCell className="text-end">
                  {rows.reduce((s, r) => s + (r.quantity || 0), 0).toFixed(2)}
                </TableCell>
                <TableCell className="text-end">
                  {rows.reduce((s, r) => s + (r.times_sold || 0), 0)}
                </TableCell>
                <TableCell className="text-end text-primary">
                  {formatCurrency(rows.reduce((s, r) => s + (r.revenue || 0), 0))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="weekly-report">
      {/* Toolbar */}
      <Card className="border-border/40">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => shiftWeek(-7)} data-testid="weekly-prev">
              <ChevronLeft size={18} />
            </Button>
            <div className="flex flex-col items-center px-2">
              <Input
                type="date"
                value={weekStart}
                onChange={(e) => setWeekStart(toISODate(getSundayOfWeek(new Date(`${e.target.value}T00:00:00`))))}
                className="w-44 text-center"
                data-testid="weekly-week-start"
              />
              {report && (
                <span className="text-xs text-muted-foreground mt-1" data-testid="weekly-period-label">
                  {formatDay(report.period.start)} → {formatDay(report.period.end)}
                </span>
              )}
            </div>
            <Button variant="outline" size="icon" onClick={() => shiftWeek(7)} data-testid="weekly-next">
              <ChevronRight size={18} />
            </Button>
          </div>
          <Button onClick={handlePrint} disabled={!report || loading} data-testid="weekly-print">
            <Printer size={16} className="me-2" /> {T('طباعة PDF', 'Print / PDF')}
          </Button>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      )}

      {error && !loading && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4 text-red-700 text-sm">{error}</CardContent>
        </Card>
      )}

      {report && !loading && (
        <div ref={printRef} className="space-y-6">
          {/* Grand totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-border/40">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{T('إجمالي المبيعات', 'Total revenue')}</p>
                <p className="text-2xl font-bold text-primary" data-testid="weekly-total-revenue">
                  {formatCurrency(report.totals.revenue)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/40">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{T('المدفوع', 'Collected')}</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(report.totals.paid)}</p>
              </CardContent>
            </Card>
            <Card className="border-border/40">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{T('المتبقي', 'Outstanding')}</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(report.totals.remaining)}</p>
              </CardContent>
            </Card>
            <Card className="border-border/40">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{T('عدد الفواتير', 'Invoices')}</p>
                <p className="text-2xl font-bold text-blue-600">{report.totals.invoice_count}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {report.totals.customer_count} {T('عملاء', 'customers')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Per-customer */}
          <Card className="border-border/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{T('المبيعات حسب العميل', 'Sales by Customer')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {report.by_customer.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground text-sm">
                  {T('لا توجد فواتير هذا الأسبوع', 'No invoices this week')}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{T('العميل', 'Customer')}</TableHead>
                      <TableHead className="text-end">{T('فواتير', 'Invoices')}</TableHead>
                      <TableHead className="text-end">{T('الإجمالي', 'Total')}</TableHead>
                      <TableHead className="text-end">{T('المدفوع', 'Paid')}</TableHead>
                      <TableHead className="text-end">{T('المتبقي', 'Remaining')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_customer.map((c) => (
                      <TableRow key={c.customer_id || '__walkin__'} data-testid={`weekly-customer-${c.customer_id || 'walkin'}`}>
                        <TableCell className="font-medium">{c.customer_name}</TableCell>
                        <TableCell className="text-end">{c.invoice_count}</TableCell>
                        <TableCell className="text-end font-bold">{formatCurrency(c.total)}</TableCell>
                        <TableCell className="text-end text-green-600">{formatCurrency(c.paid)}</TableCell>
                        <TableCell className={`text-end font-bold ${c.remaining > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                          {formatCurrency(c.remaining)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Per-item Sweets / Bakery */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderItemTable(report.by_item.Sweets || [], T('الحلويات', 'Sweets'), Cake, 'text-pink-600')}
            {renderItemTable(report.by_item.Bakery || [], T('المخبوزات', 'Bakery'), Croissant, 'text-amber-600')}
          </div>

          {(report.by_item.Uncategorized || []).length > 0 && (
            renderItemTable(report.by_item.Uncategorized, T('غير مصنّف', 'Uncategorized'), Cake, 'text-gray-500')
          )}
        </div>
      )}
    </div>
  );
}

function buildPrintHTML(report, language) {
  const isAr = language === 'ar';
  const T = (ar, en) => (isAr ? ar : en);
  const fmt = (v) => `£${Number(v || 0).toFixed(2)}`;
  const periodLine = `${report.period.start} → ${report.period.end}`;

  const customerRows = report.by_customer.map((c) => `
    <tr>
      <td>${escapeHtml(c.customer_name)}</td>
      <td style="text-align:end;">${c.invoice_count}</td>
      <td style="text-align:end;">${fmt(c.total)}</td>
      <td style="text-align:end; color:#16a34a;">${fmt(c.paid)}</td>
      <td style="text-align:end; color:${c.remaining > 0 ? '#dc2626' : '#888'}">${fmt(c.remaining)}</td>
    </tr>`).join('');

  const itemSection = (rows, title) => {
    if (!rows || rows.length === 0) return '';
    const totalQty = rows.reduce((s, r) => s + (r.quantity || 0), 0);
    const totalRev = rows.reduce((s, r) => s + (r.revenue || 0), 0);
    const totalTimes = rows.reduce((s, r) => s + (r.times_sold || 0), 0);
    return `
      <h2>${title}</h2>
      <table>
        <thead>
          <tr><th>${T('المنتج', 'Item')}</th><th style="text-align:end;">${T('الكمية', 'Qty')}</th>
          <th style="text-align:end;">${T('عدد مرات البيع', 'Times sold')}</th>
          <th style="text-align:end;">${T('الإيرادات', 'Revenue')}</th></tr>
        </thead>
        <tbody>
          ${rows.map((r) => `<tr>
            <td>${escapeHtml(r.product_name)}</td>
            <td style="text-align:end;">${r.quantity}</td>
            <td style="text-align:end;">${r.times_sold}</td>
            <td style="text-align:end; font-weight:bold;">${fmt(r.revenue)}</td>
          </tr>`).join('')}
          <tr style="background:#f5f5f5; font-weight:bold;">
            <td>${T('الإجمالي', 'Total')}</td>
            <td style="text-align:end;">${totalQty.toFixed(2)}</td>
            <td style="text-align:end;">${totalTimes}</td>
            <td style="text-align:end;">${fmt(totalRev)}</td>
          </tr>
        </tbody>
      </table>`;
  };

  return `<!DOCTYPE html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${isAr ? 'rtl' : 'ltr'}">
<head>
<meta charset="UTF-8">
<title>${T('التقرير الأسبوعي', 'Weekly Report')} - ${periodLine}</title>
<style>
  body { font-family: ${isAr ? 'Tahoma, sans-serif' : 'Arial, sans-serif'}; padding: 30px; color: #1a1a1a; }
  h1 { color: #c2185b; margin: 0 0 4px; }
  h2 { color: #c2185b; margin-top: 30px; border-bottom: 2px solid #c2185b; padding-bottom: 6px; }
  .meta { color: #666; margin-bottom: 24px; }
  .totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 16px 0 24px; }
  .totals .box { border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
  .totals .box .label { font-size: 12px; color: #666; }
  .totals .box .value { font-size: 22px; font-weight: bold; color: #c2185b; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #eee; font-size: 14px; }
  th { background: #fafafa; color: #444; text-align: ${isAr ? 'right' : 'left'}; }
  @media print { body { padding: 12px; } }
</style></head>
<body>
  <h1>${T('Qassion Bakeries - التقرير الأسبوعي', 'Qassion Bakeries — Weekly Report')}</h1>
  <div class="meta">${periodLine}</div>

  <div class="totals">
    <div class="box"><div class="label">${T('إجمالي المبيعات', 'Total revenue')}</div><div class="value">${fmt(report.totals.revenue)}</div></div>
    <div class="box"><div class="label">${T('المدفوع', 'Collected')}</div><div class="value" style="color:#16a34a;">${fmt(report.totals.paid)}</div></div>
    <div class="box"><div class="label">${T('المتبقي', 'Outstanding')}</div><div class="value" style="color:#ea580c;">${fmt(report.totals.remaining)}</div></div>
    <div class="box"><div class="label">${T('عدد الفواتير', 'Invoices')}</div><div class="value" style="color:#2563eb;">${report.totals.invoice_count}</div></div>
  </div>

  <h2>${T('المبيعات حسب العميل', 'Sales by Customer')}</h2>
  ${report.by_customer.length === 0
    ? `<p style="color:#666;">${T('لا توجد فواتير', 'No invoices')}</p>`
    : `<table>
        <thead><tr>
          <th>${T('العميل', 'Customer')}</th>
          <th style="text-align:end;">${T('فواتير', 'Invoices')}</th>
          <th style="text-align:end;">${T('الإجمالي', 'Total')}</th>
          <th style="text-align:end;">${T('المدفوع', 'Paid')}</th>
          <th style="text-align:end;">${T('المتبقي', 'Remaining')}</th>
        </tr></thead>
        <tbody>${customerRows}</tbody>
      </table>`}

  ${itemSection(report.by_item.Sweets, T('الحلويات', 'Sweets'))}
  ${itemSection(report.by_item.Bakery, T('المخبوزات', 'Bakery'))}
  ${itemSection(report.by_item.Uncategorized, T('غير مصنّف', 'Uncategorized'))}
</body></html>`;
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
